import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plane,
  Car,
  MapPin,
  Clock,
  Shield,
  Baby,
  CheckCircle,
  Star,
  ArrowRight,
  Users,
  Phone,
} from "lucide-react";
import { Button } from "../components/ui/button";
import BookingWidget from "../components/booking/BookingWidget";
import heroImage from "../assets/homeHero.webp";
import Airport from "../assets/airportHome.webp";
import city from "../assets/cityHome.webp";
import disneyland from "../assets/disneyHome.webp";
import homeParis2 from "../assets/homeParis2.webp";
import homeDisney1 from "../assets/homeDisney1.webp";
import homeDisney3 from "../assets/homeDisney3.webp";
import homeVersaillers4 from "../assets/homeVersaillers4.webp";
import homeParis4 from "../assets/homeParis4.webp";
import homeParis5 from "../assets/homeParis5.webp";
import homeLuxury from "../assets/homeLuxury.webp";

const Home = () => {
  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] lg:min-h-screen flex items-center pt-24 pb-16 lg:pt-32 lg:pb-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Paris Eiffel Tower"
            className="w-full h-full object-cover"
          />
          {/* Lighter gradient to show more of the background image */}
          <div className="absolute inset-0 bg-gradient-to-r from-midnight-navy/90 via-midnight-navy/70 to-midnight-navy/50"></div>
        </div>

        <div className="container mx-auto px-4 lg:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-champagne-gold/20 text-champagne-gold rounded-full text-sm font-medium mb-4 lg:mb-6">
                <Star className="w-4 h-4 fill-current" />
                Rated 4.9/5 by happy travelers
              </span>

              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight mb-4 lg:mb-6">
                Premium Paris{" "}
                <span className="text-champagne-gold">Airport Transfers</span>{" "}
                &amp; Disneyland Rides
              </h1>

              <p className="text-base md:text-lg text-paris-stone/90 mb-6 leading-relaxed max-w-xl">
                Private transfers from CDG, Orly &amp; Beauvais airports to
                Disneyland Paris, Paris City &amp; beyond. Fixed prices, free
                child seats, pay driver on arrival.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 md:gap-4 mb-6">
                <div className="flex items-center gap-2 text-white text-sm">
                  <Baby className="w-4 h-4 text-champagne-gold" />
                  <span>Free Child Seats</span>
                </div>
                <div className="flex items-center gap-2 text-white text-sm">
                  <Shield className="w-4 h-4 text-champagne-gold" />
                  <span>Fixed Prices</span>
                </div>
                <div className="flex items-center gap-2 text-white text-sm">
                  <CheckCircle className="w-4 h-4 text-champagne-gold" />
                  <span>Pay Driver</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 lg:gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-6 py-5 lg:px-8 lg:py-6 rounded-full shadow-lg hover:shadow-xl transition-all text-base lg:text-lg"
                  data-testid="hero-book-now-btn"
                >
                  <Link to="/booking">
                    Book Now
                    <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white hover:text-midnight-navy font-semibold px-6 py-5 lg:px-8 lg:py-6 rounded-full transition-all text-base lg:text-lg"
                  data-testid="hero-pricing-btn"
                >
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>

              {/* Phone Number */}
              <div className="mt-6 lg:mt-8 flex items-center gap-3">
                <a
                  href="tel:+33763519524"
                  className="flex items-center gap-2 text-paris-stone hover:text-champagne-gold transition-colors"
                >
                  <div className="p-2 bg-champagne-gold/20 rounded-full">
                    <Phone className="w-4 h-4 lg:w-5 lg:h-5 text-champagne-gold" />
                  </div>
                  <div>
                    <p className="text-xs lg:text-sm text-paris-stone/70">
                      Call us 24/7
                    </p>
                    <p className="font-semibold text-white text-sm lg:text-base">
                      +33 763 519 524
                    </p>
                  </div>
                </a>
              </div>
            </motion.div>

            {/* Booking Widget - Desktop only */}
            <div className="hidden lg:block py-8">
              <BookingWidget variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Booking Widget - Fixed overlap issue */}
      <section className="lg:hidden px-4 py-8 bg-paris-stone relative z-20">
        <BookingWidget variant="hero" />
      </section>

      {/* Services Section */}
      <section
        className="py-16 md:py-24 bg-white"
        data-testid="services-section"
      >
        <div className="container mx-auto px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-champagne-gold font-semibold mb-4 block">
              OUR SERVICES
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Premium Transfer Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the perfect transfer service for your Paris adventure
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Disneyland Transfers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group bg-white rounded-2xl shadow-soft hover:shadow-strong transition-all duration-300 overflow-hidden border border-slate-100"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={disneyland}
                  alt="Disneyland Paris"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-midnight-navy rounded-xl flex items-center justify-center mb-4">
                  <Star className="w-6 h-6 text-champagne-gold" />
                </div>
                <h3 className="font-heading font-bold text-xl text-midnight-navy mb-2">
                  Disneyland Transfers
                </h3>
                <p className="text-slate-600 mb-4">
                  Direct transfers from CDG, Orly &amp; Paris to Disneyland.
                  Free child seats included.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-midnight-navy font-bold text-lg">
                    From €75
                  </span>
                  <Button
                    asChild
                    variant="ghost"
                    className="text-midnight-navy hover:text-midnight-navy hover:bg-paris-stone font-semibold"
                    data-testid="service-disneyland-btn"
                  >
                    <Link to="/disneyland-transfers">
                      Learn More <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Airport Transfers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group bg-white rounded-2xl shadow-soft hover:shadow-strong transition-all duration-300 overflow-hidden border border-slate-100"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={Airport}
                  alt="Airport Transfer"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-midnight-navy rounded-xl flex items-center justify-center mb-4">
                  <Plane className="w-6 h-6 text-champagne-gold" />
                </div>
                <h3 className="font-heading font-bold text-xl text-midnight-navy mb-2">
                  Airport Transfers
                </h3>
                <p className="text-slate-600 mb-4">
                  CDG, Orly &amp; Beauvais airport pickups. Flight tracking
                  &amp; meet &amp; greet service.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-midnight-navy font-bold text-lg">
                    From €70
                  </span>
                  <Button
                    asChild
                    variant="ghost"
                    className="text-midnight-navy hover:text-midnight-navy hover:bg-paris-stone font-semibold"
                    data-testid="service-airport-btn"
                  >
                    <Link to="/airport-transfers">
                      Learn More <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* City Tours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group bg-white rounded-2xl shadow-soft hover:shadow-strong transition-all duration-300 overflow-hidden border border-slate-100"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={city}
                  alt="City Tour"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-midnight-navy rounded-xl flex items-center justify-center mb-4">
                  <Car className="w-6 h-6 text-champagne-gold" />
                </div>
                <h3 className="font-heading font-bold text-xl text-midnight-navy mb-2">
                  City Tours
                </h3>
                <p className="text-slate-600 mb-4">
                  Hourly private tours around Paris, Versailles &amp; beyond.
                  Customized itineraries.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-midnight-navy font-bold text-lg">
                    €70/hr
                  </span>
                  <Button
                    asChild
                    variant="ghost"
                    className="text-midnight-navy hover:text-midnight-navy hover:bg-paris-stone font-semibold"
                    data-testid="service-tours-btn"
                  >
                    <Link to="/city-tours">
                      Learn More <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Luxury Van Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-midnight-navy to-slate-900">
        <div className="container mx-auto px-4 lg:px-6 ">
          <div className="grid lg:grid-cols-2 gap-14 items-center ">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={homeLuxury}
                alt="Luxury Van Service"
                className="rounded-3xl shadow-2xl object-cover w-full h-full"
              />
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-white max-w-xl"
            >
              {/* Label */}
              <span className="text-champagne-gold font-semibold tracking-wide mb-3 block">
                PREMIUM VEHICLE OPTION
              </span>

              {/* Heading */}
              <h2 className="font-heading font-bold text-3xl md:text-4xl leading-tight mb-4">
                Luxury Van Upgrade
              </h2>

              {/* Intro */}
              <p className="text-paris-stone/80 text-lg leading-relaxed mb-8">
                Choose our{" "}
                <span className="text-white font-semibold">Luxury Van</span> for
                added comfort, more space, and a refined travel experience
                across all airport, city, and Disneyland transfers.
              </p>

              {/* Features – clean vertical list */}
              <ul className="space-y-3 mb-8">
                {[
                  "Spacious interior with extra legroom",
                  "Premium seating for a smoother ride",
                  "Ideal for families and small groups",
                  "Mercedes-Benz V-Class equipped with a TV",
                  "Porter service",
                  "Starry lights",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-champagne-gold mt-0.5" />
                    <span className="text-paris-stone/90">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Price – responsive */}
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-8">
                <span className="text-sm uppercase tracking-wide text-paris-stone/60">
                  Supplement
                </span>

                {/* Divider – desktop only */}
                <div className="hidden md:block h-px flex-1 bg-white/20" />

                <span className="text-white">
                  <span className="text-champagne-gold font-bold text-xl">
                    €10
                  </span>{" "}
                  <span className="text-paris-stone/80">per destination</span>
                </span>
              </div>

              {/* Instruction – soft callout */}
              <div className="bg-white/5 rounded-xl p-4 mb-10">
                <p className="text-sm text-paris-stone/80 leading-relaxed">
                  <span className="font-semibold text-white">Important:</span>{" "}
                  Please mention{" "}
                  <span className="font-semibold text-white">“Luxury Van”</span>{" "}
                  in the{" "}
                  <span className="font-semibold text-white">
                    Special Request
                  </span>{" "}
                  field when completing the booking form.
                </p>
              </div>

              {/* CTA */}
              <Button
                asChild
                size="lg"
                className="w-full md:w-auto bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all text-lg"
              >
                <Link
                  to="/booking"
                  className="flex items-center justify-center"
                >
                  Proceed to Booking
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section
        className="py-16 md:py-24 bg-paris-stone"
        data-testid="how-it-works-section"
      >
        <div className="container mx-auto px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-champagne-gold font-semibold mb-4 block">
              HOW IT WORKS
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Book in 3 Simple Steps
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: "1",
                title: "Choose Your Route",
                description:
                  "Select pickup and drop-off locations, date, time, and passengers.",
                icon: MapPin,
              },
              {
                step: "2",
                title: "Get Instant Quote",
                description:
                  "See your fixed price instantly. No hidden fees, no surprises.",
                icon: Clock,
              },
              {
                step: "3",
                title: "Pay Your Driver",
                description:
                  "Pay directly to your driver by card or cash on arrival.",
                icon: CheckCircle,
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="relative inline-flex mb-6">
                  <div className="w-16 h-16 bg-midnight-navy rounded-2xl flex items-center justify-center shadow-md">
                    <item.icon className="w-8 h-8 text-champagne-gold" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-champagne-gold rounded-full flex items-center justify-center text-midnight-navy font-bold text-sm shadow-md">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-midnight-navy mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section
        className="py-16 md:py-24 bg-white"
        data-testid="popular-routes-section"
      >
        <div className="container mx-auto px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-champagne-gold font-semibold mb-4 block">
              POPULAR ROUTES
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Most Booked Transfers
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                from: "CDG Airport",
                to: "Disneyland Paris",
                price: 75,
                time: "~45 min",
                image: homeDisney1,
              },
              {
                from: "CDG Airport",
                to: "Paris City",
                price: 70,
                time: "~50 min",
                image: homeParis2,
              },
              {
                from: "Orly Airport",
                to: "Disneyland Paris",
                price: 90,
                time: "~55 min",
                image: homeDisney3,
              },
              {
                from: "Disneyland Paris",
                to: "Paris City",
                price: 80,
                time: "~45 min",
                image: homeParis4,
              },
              {
                from: "Paris City",
                to: "Versailles",
                price: 70,
                time: "~40 min",
                image: homeVersaillers4,
              },
              {
                from: "Beauvais Airport",
                to: "Paris City",
                price: 170,
                time: "~90 min",
                image: homeParis5,
              },
            ].map((route, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group bg-white rounded-2xl shadow-soft hover:shadow-strong transition-all duration-300 overflow-hidden border border-slate-100"
              >
                <Link
                  to={`/booking?pickup=${encodeURIComponent(route.from)}&dropoff=${encodeURIComponent(route.to)}`}
                  className="block bg-paris-stone hover:bg-midnight-navy/5 rounded-xl border border-slate-200 hover:border-champagne-gold transition-all group overflow-hidden"
                  data-testid={`route-${index}`}
                >
                  {/* Bigger Image */}
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={route.image}
                      alt="Luxury Van Service"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-midnight-navy rounded-full flex items-center justify-center group-hover:bg-champagne-gold transition-colors">
                          <Car className="w-5 h-5 text-champagne-gold group-hover:text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-midnight-navy">
                            {route.from}
                          </p>
                          <p className="text-sm text-slate-500">
                            to {route.to}
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-champagne-gold transition-colors" />
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {route.time}
                      </span>
                      <span className="font-heading font-bold text-xl text-midnight-navy">
                        €{route.price}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section
        className="py-16 md:py-24 bg-midnight-navy"
        data-testid="trust-section"
      >
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              {
                icon: Baby,
                title: "Free Child Seats",
                desc: "Infants & toddlers",
              },
              { icon: Shield, title: "Fixed Prices", desc: "No hidden fees" },
              { icon: Users, title: "Meet & Greet", desc: "Name sign pickup" },
              { icon: Clock, title: "24/7 Support", desc: "Always available" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-white"
              >
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-champagne-gold" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-1">
                  {item.title}
                </h3>
                <p className="text-paris-stone/80">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section
        className="py-16 md:py-24 bg-paris-stone"
        data-testid="testimonials-section"
      >
        <div className="container mx-auto px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-champagne-gold font-semibold mb-4 block">
              TESTIMONIALS
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              What Our Customers Say
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: "Sarah M.",
                location: "London, UK",
                text: "Perfect service! Driver was waiting at CDG with our name sign. Kids loved the free car seats. Highly recommend!",
                rating: 5,
              },
              {
                name: "Michael R.",
                location: "New York, USA",
                text: "Fixed price was exactly what we paid. No surprises, no hidden fees. Will definitely use again on our next Disneyland trip.",
                rating: 5,
              },
              {
                name: "Emma L.",
                location: "Sydney, Australia",
                text: "24/7 support was amazing when our flight was delayed. They tracked it and the driver was there when we landed. 5 stars!",
                rating: 5,
              },
            ].map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-soft"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-champagne-gold fill-current"
                    />
                  ))}
                </div>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  "{review.text}"
                </p>
                <div>
                  <p className="font-semibold text-midnight-navy">
                    {review.name}
                  </p>
                  <p className="text-sm text-slate-500">{review.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-16 md:py-24 bg-gradient-to-br from-midnight-navy to-slate-900"
        data-testid="cta-section"
      >
        <div className="container mx-auto px-4 lg:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Ready to Book Your Transfer?
            </h2>
            <p className="text-lg text-paris-stone/80 mb-8 max-w-2xl mx-auto">
              Get an instant quote and book your premium transfer to Disneyland
              Paris today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all text-lg"
                data-testid="cta-book-btn"
              >
                <Link to="/booking">
                  Book Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white/30 text-white hover:bg-white hover:text-midnight-navy font-semibold px-8 py-6 rounded-full transition-all text-lg"
              >
                <Link to="/pricing">View All Prices</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
